import React, { useMemo } from "react";
import { connect } from "react-redux";
import { merge, combineLatest, BehaviorSubject } from "rxjs";
import {
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  debounceTime,
  auditTime,
  takeUntil,
} from "rxjs/operators";
import xstream from "state-x-stream";
import PropTypes from "prop-types";
import eventEmitter from "../../eventEmitter";
import HelpModal from "./HelpModal";
import LayoutOptions from "./LayoutOptions";
import Card from "../Card";
import GroupedCards from "./GroupedCards";
import { cards as variantCards } from "../cards";
import { cards as geneCards } from "../geneCards";
import ViewAllButton from "./ViewAllButton";
import { getSelectedCard } from "../../../state/selectors/CardsUI.selectors";

function controller({ props$, destroyed$ }) {
  const scrollTop$ = new BehaviorSubject(window.scrollY);
  const { resizeTracker$, scrollTracker$ } = eventEmitter;
  const collapsedHeight = 55;

  scrollTracker$
    .pipe(auditTime(10), takeUntil(destroyed$))
    .subscribe(() =>
      requestAnimationFrame(() => scrollTop$.next(window.scrollY))
    );

  const compactChanged$ = props$.pipe(
    map(({ isCompact }) => isCompact),
    distinctUntilChanged()
  );

  const selectedCardDimensions$ = merge(
    compactChanged$.pipe(debounceTime(100)),
    resizeTracker$.pipe(debounceTime(100))
  ).pipe(
    switchMap(() => props$),
    map(({ selectedCard }) => selectedCard),
    distinctUntilChanged(),
    map((selectedCard) => document.getElementById(selectedCard)),
    filter(Boolean),
    map((cardEl) => {
      const cardColumn = cardEl.parentNode;
      const cardLeft = cardColumn.offsetLeft - cardColumn.parentNode.offsetLeft;
      const cardOffsetTop = cardEl.offsetTop;
      return { cardOffsetTop, cardLeft };
    })
  );

  const cardLeft$ = selectedCardDimensions$.pipe(
    filter(Boolean),
    map((x) => x.cardLeft),
    distinctUntilChanged()
  );

  const containerHeight$ = merge(resizeTracker$, compactChanged$).pipe(
    debounceTime(200),
    map(() => document.getElementById("cardsContainer")),
    filter(Boolean),
    map((el) => el.offsetHeight)
  );

  const cardTop$ = selectedCardDimensions$.pipe(
    map(({ cardOffsetTop }) => cardOffsetTop),
    distinctUntilChanged()
  );

  const shouldCollapse$ = combineLatest([containerHeight$, scrollTop$]).pipe(
    map(([contHeight, scrollTop]) => contHeight - scrollTop < collapsedHeight),
    distinctUntilChanged()
  );

  const transformY$ = combineLatest([
    scrollTop$,
    containerHeight$,
    cardTop$,
  ]).pipe(
    map(([scrollTop, containerHeight, cardTop]) => {
      const offset = Math.min(containerHeight - collapsedHeight, scrollTop);
      const parentY = scrollTop ? -offset : 0;
      const containerY = Math.max(-cardTop - parentY, 0);
      return { parentY, containerY };
    })
  );

  const parentY$ = transformY$.pipe(
    map((x) => x.parentY),
    distinctUntilChanged()
  );

  const containerY$ = transformY$.pipe(
    map((x) => x.containerY),
    distinctUntilChanged()
  );

  return {
    parentY: parentY$,
    containerY: containerY$,
    shouldCollapse: shouldCollapse$,
    cardLeft: cardLeft$,
    collapsedHeight,
  };
}

const CardsContainerTop = ({
  shouldCollapse,
  cards,
  selectedCard,
  cardLeft,
  parentY,
  containerY,
  collapsedHeight,
}) => {
  const CardEl = useMemo(() => {
    const selCard = variantCards[selectedCard] || geneCards[selectedCard];
    const CardComp = (selCard || {}).Card;
    return CardComp ? <CardComp card={selCard} id="fake" /> : null;
  }, [cards, selectedCard]);

  const parentBorderClass = "tw-border-b tw-border-blueGray-300";
  const parentBgClass = "tw-bg-blueGray-50 backdrop-blur tw-bg-opacity-80";
  const parentClasses = "tw-py-2 tw-pointer-events-auto tw-overflow-hidden ";
  const parentShadowClass = shouldCollapse ? "tw-shadow-xl" : "";

  const parentStyle = {
    transform: `translateY(${parentY}px)`,
    minHeight: collapsedHeight,
  };
  const containerStyle = { transform: `translateY(${containerY}px)` };
  const collapsedStyle = {
    opacity: shouldCollapse ? 0 : 1,
    transform: `translateX(${shouldCollapse ? -cardLeft : 0}px)`,
  };

  return (
    <div
      className="tw-pointer-events-none tw-z-0 tw-overflow-hidden"
      id="cardsContainer"
    >
      <div
        className={`${parentShadowClass} ${parentBgClass} ${parentBorderClass} ${parentClasses}`}
        style={parentStyle}
      >
        <div
          className="tw-mx-auto tw-max-w-1800 tw-px-10 tw-overflow-hidden"
          style={containerStyle}
        >
          <div
            className="transition-all transition-linear transition-duration-200"
            tabIndex={-1}
            style={collapsedStyle}
          >
            <GroupedCards isCollapsed={shouldCollapse} cards={cards} />
          </div>
        </div>
      </div>

      <div className="tw-mx-auto tw-max-w-1800 tw-px-10 tw-absolute tw-top-0 tw-inset-x-0 tw-z-10">
        <div className="tw-absolute tw-right-0 tw-top-0 tw-pointer-events-auto">
          <div className="tw-pointer-events-auto tw-mr-2">
            <LayoutOptions />
            <HelpModal />
          </div>
        </div>
      </div>

      {shouldCollapse ? (
        <div className="tw-absolute tw-inset-0 tw-py-2 top-compact tw-pointer-events-auto tw-top-0 tw-opacity-100 anim-fade-in anim-duration-200 tw-h-1">
          <div className="tw-mx-auto tw-max-w-1800 tw-px-10 tw-relative">
            <div className="tw-flex tw-w-80 side-container anim-fade-in anim-duration-200 ">
              <div className="tw-flex-grow">{CardEl || null}</div>
              <ViewAllButton />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

CardsContainerTop.propTypes = {
  cards: PropTypes.arrayOf(PropTypes.instanceOf(Card)),
  shouldCollapse: PropTypes.bool,
  parentY: PropTypes.number,
  containerY: PropTypes.number,
  selectedCard: PropTypes.string,
  cardLeft: PropTypes.number,
  collapsedHeight: PropTypes.number.isRequired,
};

CardsContainerTop.defaultProps = {
  parentY: 0,
  containerY: 0,
  shouldCollapse: true,
  cards: [],
  selectedCard: "",
  cardLeft: 0,
};

const mapStateToProps = (state) => {
  return {
    isCompact: state.cardOptions.isCompact,
    selectedCard: getSelectedCard(state),
  };
};

export default connect(mapStateToProps)(xstream(controller)(CardsContainerTop));
